'use client'

import { useCallback, useEffect, useRef } from 'react'

import { useSocialFeedInfinite } from '../../hooks/useSocialFeedInfinite'
import { SocialFeedActivityCard } from '../SocialFeedActivityCard'

import { getFeedItemKey } from '@/features/profiles/types/feed'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { Page } from '@/shared/ui/Page'


function FeedSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="flex gap-2 rounded-xl border border-white/8 bg-[linear-gradient(168deg,#3d3d5c_0%,#2a2a42_50%,#252538_100%)] p-2.5 shadow-[0_4px_0_0_rgba(0,0,0,0.48),0_10px_22px_-8px_rgba(0,0,0,0.48)] ring-1 ring-inset ring-white/10 sm:gap-2.5 sm:p-3"
        >
          <div className="size-6 shrink-0 animate-pulse rounded-full bg-white/10" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
            <div className="h-3.5 w-32 animate-pulse rounded bg-white/12 sm:h-4 sm:w-36" />
            <div className="h-2.5 w-24 animate-pulse rounded bg-white/8 sm:h-3 sm:w-28" />
            <div className="mt-1 h-8 w-full animate-pulse rounded-md bg-white/6 sm:h-9" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function SocialFeedPage() {
  const {
    records,
    isPending,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useSocialFeedInfinite()

  const sentinelRef = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  return (
    <Page>
      <Card variant="dark" className="max-w-2xl mx-auto w-full">
        <CardHeader>
          <CardTitle>Novidades</CardTitle>
        </CardHeader>
        <CardBody>
          {isPending && <FeedSkeleton />}

          {isError && (
            <div
              className="flex flex-col items-center gap-3 rounded-2xl border border-red-500/30 bg-red-950/25 px-4 py-8 text-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]"
              role="alert"
            >
              <p className="text-sm font-medium text-red-100/95">
                Não foi possível carregar o feed.
              </p>
              <p className="text-xs text-red-200/75">
                {error instanceof Error ? error.message : 'Erro de rede'}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => refetch()}
              >
                Tentar de novo
              </Button>
            </div>
          )}

          {!isPending && !isError && records.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/4 px-6 py-14 text-center">
              <p className="text-base font-bold text-white">
                Nada novo por aqui
              </p>
              <p className="max-w-sm text-sm leading-relaxed text-[#9a9ab0]">
                Quando houver atividades da sua rede, elas aparecerão nesta
                linha do tempo.
              </p>
            </div>
          )}

          {!isPending && !isError && records.length > 0 && (
            <ul
              className="flex list-none flex-col gap-2 p-0 sm:gap-2.5 md:gap-4"
              aria-label="Atividades"
            >
              {records.map((item, index) => (
                <li key={getFeedItemKey(item)}>
                  <SocialFeedActivityCard
                    item={item}
                    style={{
                      animationDelay: `${Math.min(index, 14) * 38}ms`
                    }}
                  />
                </li>
              ))}
            </ul>
          )}

          {hasNextPage ? (
            <div
              ref={sentinelRef}
              className="h-3 w-full shrink-0"
              aria-hidden
            />
          ) : null}

          {isFetchingNextPage && (
            <p className="py-1 text-center text-xs font-medium text-[#8c8ca0]">
              Carregando…
            </p>
          )}

          {!isPending &&
            !isError &&
            records.length > 0 &&
            !hasNextPage &&
            !isFetchingNextPage && (
              <p className="pb-1 pt-2 text-center text-xs font-medium text-[#6c6c80]">
                Você viu tudo por agora.
              </p>
            )}
        </CardBody>
      </Card>
    </Page>
  )
}
